import { request, requestStream, ResponseData } from "./instance";
import duyin from "@/utils/duyin.json";
import Logger from "@/utils/Logger";
import { removeEmoji } from "@/utils/subtitleMessageHandler";
import { speedToApi } from "@/utils/speedUtils";

const streamingHeaders = {
  "Content-Type": "application/json; charset=UTF-8",
  Authorization: "Bearer;uM5bjb3CknbTRgcC8ikq6uRKRkJQTp7A",
  Accept: "application/json", // 流式响应返回JSON格式，不是直接的音频流
  "Cache-Control": "no-cache", // 禁用缓存，确保实时接收流数据
  "X-Api-App-Id": "2326791206",
  "X-Api-Key": "b929a7fe-d414-45fe-9e85-b96e45c74db3",
  "X-Api-Resource-Id": "seed-tts-1.0",
};

// 创建请求数据的模板函数，避免并发请求时数据互相覆盖
const createRequestData = () => ({
  user: {
    uid: "388808087185088",
  },
  req_params: {
    ssml: "",
    speaker: "zh_female_yingyujiaoyu_mars_bigtts",
    audio_params: {
      format: "mp3",
      sample_rate: 24000,
      speech_rate: 0,
      enable_timestamp: true,
    },
  },
  additions: {
    disable_markdown_filter: true,
  },
});

export async function asyncGetAudio(
  str: string,
  voiceConfig: any = null,
  language = "en",
  voiceRatio: number = 0
) {
  let msg = removeEmoji(str);
  if (!msg) {
    Logger.info("not valid text, no need to request audio for:", str);
    return {
      error: true,
      msg: "**空字符串，不需求请求语音**",
    };
  }

  const maxRetries = 3;
  let retryCount = 0;
  let lastError: any = null;

  while (retryCount < maxRetries) {
    const audioRes = await asyncGetAudioRequest(
      str,
      language,
      voiceConfig,
      voiceRatio
    );

    // 成功时立即返回
    if (!audioRes.error) {
      return audioRes;
    }

    // 记录最后一次错误
    lastError = audioRes;
    retryCount++;

    // 如果还有重试机会，继续重试
    if (retryCount < maxRetries) {
      Logger.info(
        `TTS request failed, retrying (${retryCount}/${maxRetries})...`
      );
      continue;
    }
  }

  // 所有重试都失败，返回最后一次的错误或默认错误
  return (
    lastError || {
      error: true,
      msg: "**语音生成错误**",
    }
  );
}

/**
 * send request to backend service to get the audio for a text
 * @param {*} str
 * @returns
 */
export async function asyncGetAudioRequest(
  str: string,
  language: string,
  voiceConfig: any,
  voiceRatio: number
) {
  let tmp_str = str;
  const duyinJson: { [key: string]: string } = duyin;

  tmp_str = tmp_str
    ?.replace(/\\n/g, "")
    .replace(/\\\\n/g, "")
    .replace(/\\"/g, "");
  // tmp_str = tmp_str?.replace(/'/g, "");
  // tmp_str = tmp_str?.replace(/`/g, ""); //remove it because it will cause error:you're doing great!  ---> youre doing great!
  // tmp_str = tmp_str?.replace(/"/g, "");
  tmp_str = tmp_str?.replace(/\\/g, "");
  // tmp_str = tmp_str?.replace(/"/g, "");
  // tmp_str = tmp_str?.replace(/"/g, "");
  tmp_str = tmp_str?.replace(/</g, "");
  tmp_str = tmp_str?.replace(/>/g, "");
  tmp_str = tmp_str?.replace(/\*/g, "");
  // tmp_str = tmp_str.replaceAll(
  //   /(\p{Emoji_Modifier_Base}\p{Emoji_Modifier}?|\p{Emoji_Presentation}|\p{Emoji}\uFE0F)/gu,
  //   ""
  // );

  tmp_str = tmp_str?.replace(
    /([\uD83C-\uDBFF\uDC00-\uDFFF]+|[\u2694-\u2697\u2699\u269B\u269C\u26A0\u26A1\u26AA\u26AB\u26B0\u26B1\u26BD\u26BE\u26C4\u26C5\u26CE\u26CF\u26D1\u26D3\u26E9\u26EA\u26F0-\u26F5\u26F7\u26F8\u26FA\u26FD\u2702\u2705\u2708-\u270D\u270F\u2712\u2714\u2716\u271D\u2721\u2728\u2733\u2734\u2744\u2747\u274C\u274E\u2753-\u2755\u2757\u2763\u2764\u2795-\u2797\u27A1\u27B0\u27BF\u2934\u2935\u2B05-\u2B07\u2B1B\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299])/g,
    ""
  );

  for (var key in duyin) {
    tmp_str = tmp_str?.replace(key, duyinJson[key]);
  }

  tmp_str = `<speak>${tmp_str}</speak>`;
  // const numberRegex = /[-+]?\d*\.\d+/g;
  //
  // // 使用 replace 方法替换匹配到的数字
  // tmp_str = tmp_str.replace(numberRegex, (match) => {
  //   return `<say-as interpret-as="number">${match}</say-as>`;
  // });

  // 为每个请求创建独立的数据副本，避免并发请求时数据互相覆盖
  const requestData = createRequestData();
  requestData.req_params.ssml = tmp_str;
  requestData.req_params.speaker = voiceConfig?.voice_type;

  // 设置语速参数
  if (voiceRatio !== undefined && voiceRatio !== null) {
    const speedValue = speedToApi(voiceRatio);
    requestData.req_params.audio_params.speech_rate = speedValue;
  }

  // if (voiceConfig && voiceConfig?.emotion)
  //   requestData.audio["emotion"] = voiceConfig?.emotion;

  let lan = language
    ? language
    : voiceConfig && voiceConfig.language
    ? voiceConfig.language
    : "en";

  // if (lan === "en") {
  //   requestData.audio["speed_ratio"] = 0.9;
  // }

  try {
    // 使用流式请求处理逐行JSON响应
    let response = await requestStream(
      "tts",
      {
        method: "post",
        data: requestData,
        headers: streamingHeaders,
        prefixUrl: "/",
      },
      undefined, // onChunk回调，如果需要实时处理每个chunk可以传入
      true // 流式请求不设置超时
    );

    // 流式响应返回格式：{ code, message, data (拼接后的base64), sentence, audioChunks }
    if (response && response.data) {
      let duration = "0";
      let frontend = null;

      if (response.sentence?.words && response.sentence.words.length > 0) {
        // 计算duration：取最后一个word的endTime
        const lastWord =
          response.sentence.words[response.sentence.words.length - 1];
        duration = lastWord.endTime?.toString() || "0";

        // 将sentence信息转换为frontend格式（JSON字符串）
        frontend = JSON.stringify(response.sentence);
      }

      return {
        code: response.code,
        message: response.message,
        data: response.data,
        sentence: response.sentence,
        addition: {
          duration: duration,
          frontend: frontend,
        },
      };
    } else {
      return {
        error: true,
        msg: "**语音生成错误**",
      };
    }
  } catch (error) {
    Logger.error("TTS request error:", error);
    return {
      error: true,
      msg: "**语音生成错误**",
    };
  }
}
